
import type { QueriesByCategoryTotalsData } from '@empathybroker/tsclient-stats/dist/models/queries-by-category-totals/queries-by-category-totals-data'
import type { TimelySearchData } from '@empathybroker/tsclient-stats/dist/models/timely-search/timely-search-data'
import type { PropType } from 'vue'
import type {
  CategoriesTreeItem,
  Dictionary,
  RegularFilterSelectedValues,
  WidgetOptions,
  WidgetOptionsItem,
} from '../../models'
import type { DistData } from '../insights/analytics/funnel-chart.component.vue'
import type { NoDataMessage } from './no-data.component.vue'
import { StatsReportsIdsEnum } from '@empathybroker/tsclient-stats/dist/enums/stats-reports-ids.enum'
import { defineComponent } from 'vue'
import { useKpis } from '../../composables/use-kpis'
import { ColorEnum, FeatureIdEnum, KpiEnum } from '../../enums'
import { capitalizeSentence } from '../../filters'
import { InsightsMixin, SteppedAnimationMixin } from '../../mixins'
import FunnelChart from '../insights/analytics/funnel-chart.component.vue'
import { EpbText } from '../shared/epb-text'
import Widget from '../widget.component.vue'
import NoData from './no-data.component.vue'

export default defineComponent({
  name: 'KpisWidget',
  components: {
    Widget,
    FunnelChart,
    EpbText,
    NoData,
  },
  mixins: [InsightsMixin, SteppedAnimationMixin],
  props: {
    setup: Object as PropType<{ options: WidgetOptions }>,
    period: {
      type: Object as PropType<{ startDate: Date; endDate: Date }>,
      required: true,
    },
    filters: Object as PropType<Record<string, RegularFilterSelectedValues>>,
    category: Object as PropType<CategoriesTreeItem>,
    noData: Object as PropType<Dictionary<string>>,
  },
  setup() {
    const { supportedKpis } = useKpis()
    return {
      supportedKpis,
      ColorEnum,
    }
  },
  data(): Record<string, any> {
    const messages = this.$epb.messages.highlights.widget.kpis
    return {
      baseClass: 'epb-kpis-widget',
      loadingData: true,
      title: messages.title,
      feature: FeatureIdEnum.OVERALL,
      pivotedKpis: [KpiEnum.CHECKOUT, KpiEnum.NO_RESULTS],
      animationStepDurationsInMillis: [
        SteppedAnimationMixin.data().animationStepDurationsInMillis[0],
        SteppedAnimationMixin.data().animationStepDurationsInMillis[1] +
          SteppedAnimationMixin.data().animationStepDurationsInMillis[2],
      ],
      messages,
    }
  },
  computed: {
    noDataMessage(): NoDataMessage {
      return {
        title: this.noData.title,
        ...(this.filters && { subtitle: this.noData.filters }),
        ...(this.category && { subtitle: this.noData.category }),
      }
    },
    hasDataLoaded(): boolean {
      return !!this.data?.dist?.queryCount
    },
    displayedFilterOptions(): WidgetOptions {
      return {
        ...this.filterOptions,
        items: this.filterOptions.items.filter(
          ({ id }: WidgetOptionsItem) =>
            id !== KpiEnum.QUERIES && (this.category ? !this.pivotedKpis.includes(id) : true),
        ),
      }
    },
    filterOptions(): WidgetOptions {
      let items: WidgetOptionsItem[], options: Partial<WidgetOptions>
      if (this.setup?.options) {
        options = { ...this.setup.options }
        items = this.filterBySupportedKpis(this.setup.options.items)
      } else {
        options = { multiSelect: true, documentationLink: this.messages.link }
        items = this.supportedKpis.map((kpi: KpiEnum) => ({
          id: kpi,
          label: capitalizeSentence(this.getKpiLabel(kpi)),
          selected: true,
        }))
      }
      return {
        ...options,
        items,
      }
    },
    visibleKpis(): KpiEnum[] {
      return this.filterOptions.items
        .filter(
          ({ id, selected }: WidgetOptionsItem) =>
            selected && (this.category ? !this.pivotedKpis.includes(id) : true),
        )
        .map(({ id }: WidgetOptionsItem) => id)
    },
    periodString(): string {
      const startDate = new Date(this.period.startDate)
      const endDate = new Date(this.period.endDate)
      endDate.setUTCDate(endDate.getUTCDate() - 1)
      const dateFormatOptions: Intl.DateTimeFormatOptions = {
        month: 'short',
        day: '2-digit',
      }
      const startDateString = startDate.toLocaleDateString(this.$epb.locale, dateFormatOptions)
      const endDateString = endDate.toLocaleDateString(this.$epb.locale, dateFormatOptions)
      return `${startDateString} - ${endDateString}`
    },
  },
  watch: {
    filters: {
      async handler(
        newFilters: Record<string, RegularFilterSelectedValues>,
        oldFilters: Record<string, RegularFilterSelectedValues>,
      ): Promise<void> {
        if (JSON.stringify(newFilters) !== JSON.stringify(oldFilters)) {
          await this.loadData()
        }
      },
      deep: true,
    },
    category: {
      async handler(
        newCategory: CategoriesTreeItem,
        oldCategory: CategoriesTreeItem,
      ): Promise<void> {
        if (JSON.stringify(newCategory) !== JSON.stringify(oldCategory)) {
          await this.loadData()
        }
      },
      deep: true,
    },
  },
  methods: {
    getSelectedOptions(options: WidgetOptions): WidgetOptions {
      const extraItems = this.filterOptions.items.filter(
        (item: WidgetOptionsItem) =>
          !options.items.find((updatedItem: WidgetOptionsItem) => updatedItem.id === item.id),
      )
      return { ...options, items: [...options.items, ...extraItems] }
    },
    getKpiLabel(kpi: KpiEnum): string {
      const kpisMessages = this.$epb.messages.insights.analytics.kpis
      if (kpi === KpiEnum.NO_RESULTS) {
        return kpisMessages.noResults.valueLabel
      }
      return kpisMessages[kpi].percentageLabel || kpisMessages[kpi].valueLabel
    },
    async fetchData(): Promise<{ dist: DistData }> {
      this.loadingData = true
      const id = this.category
        ? StatsReportsIdsEnum.QUERIES_BY_CATEGORY_TOTALS
        : StatsReportsIdsEnum.DAILY_SEARCH
      const response = await this.fetchStatsReport({
        id,
        params: {
          ...this.period,
          ...(this.filters && { filters: this.filters }),
          ...(this.category && {
            categoryId: this.category.id,
            level: this.category.level,
            ...(this.category.level > 1 && { parentId: this.category.parentId }),
          }),
        },
      })
      const data: QueriesByCategoryTotalsData | TimelySearchData = response.data.length
        ? response.data[0]
        : response.data

      this.loadingData = false
      return {
        dist: this.getDistData(data, id),
      }
    },
    getDistData(data: QueriesByCategoryTotalsData | TimelySearchData, reportId: string): DistData {
      return {
        queryCount: data.queryCount,
        clickCountPct: data.clickCountPct,
        add2cartCountPct: data.add2cartCountPct,
        checkoutCountPct: data.checkoutCountPct,
        ...(reportId === StatsReportsIdsEnum.QUERIES_BY_CATEGORY_TOTALS
          ? {
              findability: (data as QueriesByCategoryTotalsData).findabilityPct,
            }
          : {
              findability: (data as TimelySearchData).findability,
              noResultsCountPct: (data as TimelySearchData).noResultsCountPct,
            }),
      }
    },
    filterBySupportedKpis(optionItems: WidgetOptionsItem[]): WidgetOptionsItem[] {
      return optionItems.filter((kpi: WidgetOptionsItem) => this.supportedKpis.includes(kpi.id))
    },
  },
})
